import { FormControlLabel } from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import CodeIcon from "@material-ui/icons/Code";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import PermIdentityIcon from "@material-ui/icons/PermIdentity";
import React from "react";

import CustomInput from "components/CustomInput/CustomInput.js";
import CustomSelect from "components/CustomSelect/CustomSelect";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

import getObjectiveService from "services/ConfigObjective/getObjectiveService";
import getTargetAudience from "services/TargetAudience/getTargetAudience";
import isCodeandTextExist from "services/TestService/isCodeandTextExist";

const useStyles = makeStyles({
    inputAdornmentIcon: { color: "#555" },
    inputAdornmentDisabled: { color: "#b2b8be !important" },
    inputAdornment: { position: "relative" }
});

const PdfBasedTestConfig = React.forwardRef((props, ref) => {

    const classes = useStyles();
    console.log("i am heree ", props)
    /* ---------------- FETCH DROPDOWNS ---------------- */

    const [objectiveOptions, setObjectiveOptions] = React.useState([]);
    const [targetAudienceOptions, setTargetAudienceOptions] = React.useState([]);

    React.useEffect(() => {
        const loadData = async () => {
            try {
                const obj = await getObjectiveService.getObjective(true, true);
                const target = await getTargetAudience.getTargetAudience(true, true);
                setObjectiveOptions(obj["$values"] || []);
                setTargetAudienceOptions(target["$values"] || []);
            } catch (err) {
                console.log("error loading dropdowns", err);
            }
        };
        loadData();
    }, []);

    /* ---------------- STATE ---------------- */

    const [testId, setTestId] = React.useState(props?.st?.testId);

    const [testCode, setTestCode] = React.useState(props.st.testCode);
    const [testCodeState, setTestCodeState] = React.useState("");

    const [text, setText] = React.useState(props.st.text);
    const [textState, setTextState] = React.useState("");

    const [objective, setObjective] = React.useState(props.st.objective);
    const [targetAudience, setTargetAudience] = React.useState(props.st.targetAudience);

    const [testType, setTestType] = React.useState(props.st.testType ?? 3); // 3 = PDF custom

    const [description, setDescription] = React.useState(props.st.description);
    const [welcomeMessage, setWelcomeMessage] = React.useState(props.st.welcomeMessage ?? "Welcome");
    const [concludeMessage, setConcludeMessage] = React.useState(props.st.concludeMessage ?? "Thank You!");

    const [duration, setDuration] = React.useState(props.st.duration ?? 180);
    const [durationState, setDurationState] = React.useState("success");

    const [pointsPerQuestion, setPointsPerQuestion] = React.useState(props.st.pointsPerQuestion);
    const [pointsState, setPointsState] = React.useState("");

    const [status, setStatus] = React.useState(props.st.status ?? 1);
    const [showTimer, setShowTimer] = React.useState(props.st.showTimer == 2 ? true : false);
    const [questionsPerPage, setQuestionsPerPage] = React.useState(
        props.st.questionsPerPage ?? 1
    );
    const [attemptsAllowed, setAttemptsAllowed] = React.useState(
        props.st.attemptsAllowed ?? 1
    );

    /* ---------------- VALIDATIONS ---------------- */

    const verifyLength = (value, length) => {
        if (!value) return false;
        return value.length >= length;
    };

    const checkDuplicate = async () => {
        if (!testCode || !text) return;

        let isCodeExist = await isCodeandTextExist.dupulicateCodeCheck(testId ?? 0, testCode);
        let isTextExist = await isCodeandTextExist.dupulicateTextCheck(testId ?? 0, text);

        if (isCodeExist) setTestCodeState("error");
        if (isTextExist) setTextState("error");
    };

    React.useEffect(() => {
        if (!verifyLength(testCode, 3)) {
            setTestCodeState("error");
        } else {
            setTestCodeState("success");
        }

        if (!verifyLength(text, 3)) {
            setTextState("error");
        } else {
            setTextState("success");
        }

        checkDuplicate();
    }, [testCode, text]);

    React.useEffect(() => {
        if (duration > 0 && duration <= 360) {
            setDurationState("success");
        } else {
            setDurationState("error");
        }
    }, [duration]);

    React.useEffect(() => {
        if (pointsPerQuestion > 0) {
            setPointsState("success");
        } else {
            setPointsState("error");
        }
    }, [pointsPerQuestion]);

    const isValidated = () => {
        if (
            testCodeState === "success" &&
            textState === "success" &&
            durationState === "success" &&
            pointsState === "success"
        ) {
            return true;
        }

        if (testCodeState !== "success") setTestCodeState("error");
        if (textState !== "success") setTextState("error");
        if (durationState !== "success") setDurationState("error");
        if (pointsState !== "success") setPointsState("error");

        return false;
    };

    /* ---------------- WIZARD REF ---------------- */

    React.useImperativeHandle(ref, () => ({
        isValidated,
        sendState: () => ({
            testCode,
            text,
            objective,
            targetAudience,
            testType,
            description,
            welcomeMessage,
            concludeMessage,
            duration,
            showTimer: showTimer ? 2 : 1,
            status,
            pointsPerQuestion,
            questionsPerPage,
            attemptsAllowed,
            testCodeState,
            textState,
            durationState,
            pointsState,
            type: "details"
        }),
        state: {
            testCode,
            text,
            objective,
            targetAudience,
            testType,
            description,
            welcomeMessage,
            concludeMessage,
            duration,
            showTimer: showTimer ? 2 : 1,
            status,
            pointsPerQuestion,
            questionsPerPage,
            attemptsAllowed,
            testCodeState,
            textState,
            durationState,
            pointsState,
            type: "details"
        }
    }));

    /* ---------------- UI ---------------- */

    return (
        <GridContainer justify="center">

            {/* TEST CODE */}
            <GridItem xs={12} sm={5}>
                <CustomInput
                    success={testCodeState === "success"}
                    error={testCodeState === "error"}
                    labelText={<span>Test Code <small>(required)</small></span>}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: testCode,
                        maxLength: 20,
                        onChange: (e) => setTestCode(e.target.value.trim()),
                        endAdornment: (
                            <InputAdornment position="end">
                                <CodeIcon className={classes.inputAdornmentIcon} />
                            </InputAdornment>
                        )
                    }}
                />
            </GridItem>

            {/* TEST NAME */}
            <GridItem xs={12} sm={5}>
                <CustomInput
                    success={textState === "success"}
                    error={textState === "error"}
                    labelText={<span>Test Name <small>(required)</small></span>}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: text,
                        onChange: (e) => setText(e.target.value.trimStart()),
                        endAdornment: (
                            <InputAdornment position="end">
                                <PermIdentityIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </GridItem>

            {/* OBJECTIVE */}
            <GridItem xs={12} sm={5}>
                <CustomSelect
                    label="Objective"
                    options={objectiveOptions.map(x => ({
                        id: x.id,
                        name: x.valueString
                    }))}
                    value={objective}
                    setvalue={setObjective}
                />
            </GridItem>

            {/* TARGET AUDIENCE */}
            <GridItem xs={12} sm={5}>
                <CustomSelect
                    label="Target Audience"
                    options={targetAudienceOptions.map(x => ({
                        id: x.id,
                        name: x.valueString
                    }))}
                    value={targetAudience}
                    setvalue={setTargetAudience}
                />
            </GridItem>


            {/* DURATION */}
            <GridItem xs={12} sm={3}>
                <CustomInput
                    success={durationState === "success"}
                    error={durationState === "error"}
                    labelText={<span>Duration (1-360 mins)</span>}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        type: "number",
                        value: duration,
                        onChange: (e) => setDuration(e.target.value)
                    }}
                />
            </GridItem>

            {/* POINTS */}
            <GridItem xs={12} sm={3}>
                <CustomInput
                    success={pointsState === "success"}
                    error={pointsState === "error"}
                    labelText={<span>Points per Question</span>}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        type: "number",
                        value: pointsPerQuestion,
                        onChange: (e) => setPointsPerQuestion(e.target.value)
                    }}
                />
            </GridItem>

            {/* STATUS */}
            <GridItem xs={12} sm={2}>
                <CustomSelect
                    label="Status"
                    options={[
                        { id: 1, name: "Draft" },
                        { id: 2, name: "Ready" }
                    ]}
                    value={status}
                    setvalue={setStatus}
                />
            </GridItem>

            {/* SHOW TIMER */}
            <GridItem xs={12} sm={2}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showTimer}
                            onChange={(e) => setShowTimer(e.target.checked)}
                        />
                    }
                    label="Show Timer"
                />
            </GridItem>

            {/* DESCRIPTION */}
            <GridItem xs={5}>
                <CustomInput
                    labelText="Description"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: description,
                        multiline: true,
                        rows: 2,
                        onChange: (e) => setDescription(e.target.value),
                        endAdornment: (
                            <InputAdornment position="end">
                                <FormatAlignJustifyIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </GridItem>

            {/* WELCOME MESSAGE */}
            <GridItem xs={12} sm={5}>
                <CustomInput
                    labelText="Welcome Message"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: welcomeMessage,
                        multiline: true,
                        rows: 2,
                        onChange: (e) => setWelcomeMessage(e.target.value)
                    }}
                />
            </GridItem>

            {/* CONCLUDE MESSAGE */}
            {/* <GridItem xs={12} sm={4}>
                <CustomInput
                    labelText="Conclude Message"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: concludeMessage,
                        multiline: true,
                        rows: 2,
                        onChange: (e) => setConcludeMessage(e.target.value)
                    }}
                />
            </GridItem> */}

        </GridContainer>
    );
});

PdfBasedTestConfig.displayName = "PdfBasedTestConfig";
export default PdfBasedTestConfig;